import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Favorite,
  ArrowBack,
  Store,
  LocationOn,
  DeleteOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { url } from '../constants';

const FavoriteDeals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removingId, setRemovingId] = useState(null);
  
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchFavoriteDeals(0, true);
  }, []);

  useEffect(() => {
    if (!loadMoreRef.current || loadingMore || !hasMore || loading) return;

    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0,
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          console.log('📜 Loading more favorites...');
          loadMoreDeals();
        }
      });
    };

    observerRef.current = new IntersectionObserver(callback, options);
    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loading, page]);

  const fetchFavoriteDeals = async (pageNum = page, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    setError('');

    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        navigate('/login');
        return;
      }

      const skip = pageNum * 20;
      console.log(`📋 Fetching favorite deals (page ${pageNum}, skip ${skip})...`);

      const response = await fetch(`${url}/deals/favorites?skip=${skip}&limit=20`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        throw new Error('Failed to fetch favorites');
      }

      const data = await response.json();
      console.log(`✅ Fetched ${data.length} favorite deals`);

      if (data.length < 20) {
        setHasMore(false);
        console.log('🛑 No more favorites to load');
      }

      if (reset) {
        setDeals(data);
      } else {
        setDeals(prev => [...prev, ...data]);
      }
    } catch (error) {
      console.error('❌ Error fetching favorites:', error);
      setError(error.message || 'Failed to load favorites');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreDeals = useCallback(() => {
    if (loadingMore || !hasMore || loading) return;
    
    const nextPage = page + 1;
    console.log('📄 Loading next page:', nextPage);
    setPage(nextPage);
    fetchFavoriteDeals(nextPage, false);
  }, [page, loadingMore, hasMore, loading]);

  const handleRemoveFavorite = async (dealId) => {
    setRemovingId(dealId);

    try {
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${url}/deals/remove-favorite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ deal_id: dealId }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove from favorites');
      }

      setDeals(prev => prev.filter(deal => deal._id !== dealId));

      const savedFavorites = localStorage.getItem('userFavorites');
      if (savedFavorites) {
        const favorites = JSON.parse(savedFavorites);
        const updated = favorites.filter(id => id !== dealId);
        localStorage.setItem('userFavorites', JSON.stringify(updated));
      }

      console.log('✅ Removed from favorites');
    } catch (error) {
      console.error('❌ Error removing favorite:', error);
      setError('Failed to remove from favorites');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#1a7f3a' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F1F3F6', minHeight: '100vh', py: isMobile ? 0 : 3 }}>
      <Container 
        maxWidth="lg" 
        sx={{ 
          px: isMobile ? 0 : 2,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: isMobile ? 1 : 3, px: isMobile ? 2 : 0 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, py: isMobile ? 1.5 : 0 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: '#1a7f3a' }}>
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant={isMobile ? 'subtitle1' : 'h5'} fontWeight={600} sx={{ color: '#212121' }}>
                My Wishlist
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {deals.length} {deals.length === 1 ? 'item' : 'items'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, mx: isMobile ? 2 : 0 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {deals.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, px: isMobile ? 2 : 0 }}>
            <Favorite sx={{ fontSize: 80, color: '#BDBDBD', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Your wishlist is empty!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add items that you like to your wishlist
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/deals')}
              sx={{
                bgcolor: '#1a7f3a',
                textTransform: 'none',
                px: 4,
                '&:hover': { bgcolor: '#0f5328' },
              }}
            >
              Explore Deals
            </Button>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
                gap: isMobile ? 0 : 2,
              }}
            >
              {deals.map((deal) => {
                const dealId = deal._id || deal.id;
                const isRemoving = removingId === dealId;

                return (
                  <Card
                    key={dealId}
                    sx={{
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: isMobile ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                      borderRadius: isMobile ? 0 : 1,
                      border: isMobile ? 'none' : 'none',
                      borderRight: isMobile ? '2px solid #E0E0E0' : 'none',
                      borderBottom: isMobile ? '1px solid #E0E0E0' : 'none',
                      '&:hover': {
                        boxShadow: isMobile ? 'none' : '0 4px 12px rgba(0,0,0,0.15)',
                        transform: isMobile ? 'none' : 'translateY(-4px)',
                      },
                    }}
                    onClick={() => navigate(`/deal/${dealId}`)}
                  >
                    {/* Remove Button */}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFavorite(dealId);
                      }}
                      disabled={isRemoving}
                      sx={{
                        position: 'absolute',
                        top: isMobile ? 4 : 8,
                        right: isMobile ? 4 : 8,
                        zIndex: 10,
                        bgcolor: 'white',
                        width: isMobile ? 28 : 36,
                        height: isMobile ? 28 : 36,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        '&:hover': {
                          bgcolor: '#FFE5E5',
                          color: '#FF3E3E',
                        },
                      }}
                    >
                      {isRemoving ? (
                        <CircularProgress size={isMobile ? 16 : 20} />
                      ) : (
                        <DeleteOutline fontSize="small" sx={{ fontSize: isMobile ? 16 : 20 }} />
                      )}
                    </IconButton>

                    {/* Image */}
                    <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: '#F5F5F5' }}>
                      <CardMedia
                        component="img"
                        height={isMobile ? '140' : '180'}
                        image={deal.image_url}
                        alt={deal.deal_name}
                        sx={{
                          objectFit: 'cover',
                          userSelect: 'none',
                        }}
                      />

                      {/* Discount Badge */}
                      <Chip
                        label={`${deal.percentage_discount}% OFF`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: isMobile ? 4 : 8,
                          left: isMobile ? 4 : 8,
                          bgcolor: '#388E3C',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: isMobile ? '0.65rem' : '0.7rem',
                          height: isMobile ? 20 : 24,
                        }}
                      />
                    </Box>

                    {/* Content */}
                    <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                      {/* Deal Name */}
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#212121',
                          fontSize: isMobile ? '0.75rem' : '0.875rem',
                        }}
                      >
                        {deal.deal_name}
                      </Typography>

                      {/* Vendor */}
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <Store sx={{ fontSize: 12, color: '#878787' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#878787',
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {deal.vendor_name}
                        </Typography>
                      </Stack>

                      {/* Price */}
                      <Stack direction="row" spacing={0.5} alignItems="baseline" sx={{ mb: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ 
                            color: '#212121',
                            fontSize: isMobile ? '0.85rem' : '1rem',
                          }}
                        >
                          ₹{deal.discounted_price}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            textDecoration: 'line-through',
                            color: '#878787',
                            fontSize: isMobile ? '0.65rem' : '0.75rem',
                          }}
                        >
                          ₹{deal.original_price}
                        </Typography>
                      </Stack>

                      {/* Location */}
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: isMobile ? 1 : 1.5 }}>
                        <LocationOn sx={{ fontSize: 18, color: '#1a7f3a' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#878787',
                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {deal.address}
                        </Typography>
                      </Stack>

                      {/* View Button */}
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/deal/${dealId}`);
                        }}
                        sx={{
                          borderColor: '#1a7f3a',
                          color: '#1a7f3a',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: isMobile ? '0.65rem' : '0.8rem',
                          py: isMobile ? 0.4 : 0.75,
                          minHeight: isMobile ? 28 : 36,
                          '&:hover': {
                            borderColor: '#1a7f3a',
                            bgcolor: '#E8F5E9',
                          },
                        }}
                      >
                        View Deal
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            {/* Load More Trigger */}
            {hasMore && (
              <Box
                ref={loadMoreRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                  minHeight: '100px',
                  px: isMobile ? 2 : 0,
                }}
              >
                {loadingMore && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} sx={{ color: '#1a7f3a' }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading more favorites...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {!hasMore && deals.length > 0 && (
              <Box sx={{ textAlign: 'center', py: 4, px: isMobile ? 2 : 0 }}>
                <Typography variant="body2" color="text.secondary">
                  🎉 You've seen all your favorites! Total: {deals.length}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default FavoriteDeals;
